import * as React from 'react'
import { Input, Label } from '.'

export type PasswordWithConfirmProps = {
  password: string
  confirm: string
  onChange: (patch: { password?: string; confirm?: string }) => void
  required?: boolean
  className?: string
  labels?: { password?: string; confirm?: string }
  placeholders?: { password?: string; confirm?: string }
  columns?: 1 | 2
}

export function PasswordWithConfirm({ password, confirm, onChange, required, className, labels, placeholders, columns = 2 }: PasswordWithConfirmProps) {
  const [showPwd, setShowPwd] = React.useState(false)
  const [showPwd2, setShowPwd2] = React.useState(false)

  const invalidUpper = !!password && !/[A-Z]/.test(password)
  const invalidSpecial = !!password && !/[^A-Za-z0-9]/.test(password)
  const showPwdHint = !!password && (invalidUpper || invalidSpecial)

  function strengthScore(pwd: string): number {
    let s = 0
    if (pwd.length >= 8) s++
    if (/[A-Z]/.test(pwd)) s++
    if (/[a-z]/.test(pwd)) s++
    if (/\d/.test(pwd)) s++
    if (/[^A-Za-z0-9]/.test(pwd)) s++
    return Math.min(s, 5)
  }
  const score = strengthScore(password || '')
  const pct = score >= 4 ? 100 : score >= 3 ? 66 : score > 0 ? 33 : 0
  const color = score >= 4 ? 'bg-green-500' : score >= 3 ? 'bg-yellow-500' : score > 0 ? 'bg-red-500' : 'bg-transparent'
  const strengthLabel = score >= 4 ? 'Forte' : score >= 3 ? 'Média' : score > 0 ? 'Fraca' : ''

  const typedConfirm = (confirm || '').length > 0
  const matches = typedConfirm && confirm === password

  return (
    <div className={className}>
      <div className={`grid grid-cols-1 gap-2 ${columns === 2 ? 'md:grid-cols-2' : ''}`}>
        <div className="space-y-1.5">
          <Label>{labels?.password || 'Senha'}</Label>
          <div className="relative">
            <Input
              className="pr-16"
              value={password}
              onChange={(e)=> onChange({ password: e.target.value })}
              type={showPwd ? 'text' : 'password'}
              placeholder={placeholders?.password || 'Crie uma senha'}
              autoComplete="new-password"
              onCopy={(e)=> e.preventDefault()}
              onCut={(e)=> e.preventDefault()}
              onContextMenu={(e)=> e.preventDefault()}
              required={required}
            />
            <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary" onClick={()=> setShowPwd(v=>!v)}>
              {showPwd ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-foreground/10">
            <div className={`h-full transition-all duration-300 ${color}`} style={{ width: `${pct}%` }} />
          </div>
          {strengthLabel && <div className="mt-1 text-xs text-foreground/70">Força da senha: <span className="font-medium">{strengthLabel}</span></div>}
          {showPwdHint && (
            <div className="text-xs text-amber-600">
              Dica: inclua {invalidUpper ? 'uma letra maiúscula' : ''}{invalidUpper && invalidSpecial ? ' e ' : ''}{invalidSpecial ? 'um caractere especial' : ''} na sua senha.
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>{labels?.confirm || 'Confirmar senha'}</Label>
          <div className={`relative rounded-md ${typedConfirm ? (matches ? 'ring-2 ring-green-500 animate-pulse' : 'ring-2 ring-red-500 animate-pulse') : ''}`}>
            <Input
              className="pr-16"
              value={confirm}
              onChange={(e)=> onChange({ confirm: e.target.value })}
              type={showPwd2 ? 'text' : 'password'}
              placeholder={placeholders?.confirm || 'Repita a senha'}
              autoComplete="new-password"
              onPaste={(e)=> e.preventDefault()}
              onDrop={(e)=> e.preventDefault()}
              onContextMenu={(e)=> e.preventDefault()}
              required={required}
            />
            <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary" onClick={()=> setShowPwd2(v=>!v)}>
              {showPwd2 ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          {typedConfirm && (
            matches
              ? <div className="text-xs text-emerald-500">Senhas conferem</div>
              : <div className="text-xs text-destructive">Senhas não conferem</div>
          )}
        </div>
      </div>
    </div>
  )
}
